import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, timeout } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { IAuth } from '@app/pages/auth/shared/interfaces/auth.interface';

interface OperatorLoginResponse {
  accessToken: string;
  refreshToken: string;
  user: OperatorUserResponse;
}

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
  user: OperatorUserResponse;
}

interface OperatorUserResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  profileImage: string;
  zipCode: string;
  companyName: string;
  stripeCustomerId: string;
  roles: string;
  is_operator: boolean;
  operator_id: string;
  is_integrated: boolean;
  is_active: boolean;
  user_notifications: string;
  sms_notifications: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly requiredSessionVersion = 2;
  private authSubject: BehaviorSubject<IAuth>;
  public auth: Observable<IAuth>;
  public user: IAuth;

  private refreshInFlight$: Observable<IAuth> | null = null;


  constructor(private router: Router, private http: HttpClient) {
    this.authSubject = new BehaviorSubject<IAuth>(
      this.getStoredSession()
    );
    this.auth = this.authSubject.asObservable();
  }

  private getStoredSession(): IAuth | null {
    const rawSession = localStorage.getItem('buslane-session');
    if (!rawSession) {
      return null;
    }
    try {
      return JSON.parse(rawSession);
    } catch {
      return null;
    }
  }

  /**
   * @name authValue
   * @description Getter for easy access to auth subject value
   */
  public get authValue(): IAuth {
    return this.authSubject.value;
  }

  /**
   * @name login
   * @description Request login
   * @param {string} email
   * @param {string} password
   */
  login(email: string, password: string) {
    return this.http
      .post<OperatorLoginResponse>(
        `${environment.apiRoute}/auth/operator/login`,
        {
          email,
          password,
        }
      )
      .pipe(
        map((response: OperatorLoginResponse) => {
          if (response?.accessToken && response?.user) {
            this.user = this.mapOperatorUser(response.user);
            this.user.access_token = response.accessToken;
            this.user.secret = response.refreshToken;
            this.user.session_version = this.requiredSessionVersion;
            localStorage.setItem('buslane-session', JSON.stringify(this.user));
            this.authSubject.next(this.user);
            return this.user;
          }

          throw new Error('Invalid login response');
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  refreshSession(): Observable<IAuth> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const session = this.getStoredSession();
    if (!session?.email || !session?.secret) {
      return throwError(new Error('No refresh token available'));
    }

    this.refreshInFlight$ = this.http
      .post<RefreshTokenResponse>(
        `${environment.apiRoute}/auth/operator/refresh-token`,
        {
          refreshToken: session.secret,
        }
      )
      .pipe(
        timeout(15000),
        map((response) => {
          if (this.getStoredSession()?.secret !== session.secret) {
            throw new Error('Session changed during refresh');
          }
          const updatedSession: IAuth = {
            ...session,
            ...this.mapOperatorUser(response.user),
            access_token: response.accessToken,
            secret: response.refreshToken ?? session.secret,
            session_version: this.requiredSessionVersion,
          };

          localStorage.setItem(
            'buslane-session',
            JSON.stringify(updatedSession)
          );
          this.authSubject.next(updatedSession);
          this.user = updatedSession;
          return updatedSession;
        }),
        catchError((error) => {
          const status = (error as any)?.status;
          if ((status === 401 || status === 403) && this.getStoredSession()?.secret === session.secret) {
            this.clearSession();
          }
          return throwError(error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1)
      );

    return this.refreshInFlight$;
  }

  private mapOperatorUser(user: OperatorUserResponse): IAuth {
    return {
      id: user.id,
      name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      email: user.email,
      password: '',
      access_token: '',
      secret: '',
      first_name: user.firstName,
      last_name: user.lastName,
      address_1: user.address1,
      address_2: user.address2,
      zipcode: user.zipCode,
      city: user.city,
      phone: user.phoneNumber,
      state: user.state,
      company: user.companyName,
      is_operator: user.is_operator,
      operator_id: user.operator_id,
      stripe_customer_id: user.stripeCustomerId,
      roles: user.roles as IAuth['roles'],
      img_profile: user.profileImage,
      is_integrated: user.is_integrated,
      is_active: user.is_active,
      user_notifications: user.user_notifications,
      sms_notifications: user.sms_notifications,
    };
  }
  updateUserProfile(formData: any) {
    const userLS = JSON.parse(
      localStorage.getItem('buslane-session') as string
    );
    let userDetails = userLS ? userLS : null;
    return this.http.put(
      `${environment.apiUrl}/api/v1/operators/${userDetails.id}/set-profile-img`,
      formData
    );
  }
  updateCompanyLogo(formData: any, index) {
    const userLS = JSON.parse(
      localStorage.getItem('buslane-session') as string
    );
    let userDetails = userLS ? userLS : null;
    return this.http.put(
      `${environment.apiUrl}/api/v1/operators/${userDetails.operator_id}/${
        index === 0 ? 'set-company-logo' : `set-company-img${index}`
      }`,
      formData
    );
  }

  /**
   * @name logout
   * @description Remove user from local storage and set current user to null
   */
  logout() {
    // Normal logout affects this browser only; other devices share the refresh credential.
    this.clearSession();
  }

  private clearSession() {
    localStorage.removeItem('buslane-session');
    localStorage.removeItem('current_conversation');
    this.authSubject.next(null);
    this.user = null;
    this.router.navigate(['/auth/signin']);
  }

  async initializeSession(): Promise<void> {
    const session = this.getStoredSession();
    if (!session) return;
    if (session.session_version !== this.requiredSessionVersion || !session.secret || !session.access_token) {
      // A local profile from Flask is not proof of identity for Nest.
      this.clearSession();
      return;
    }
    try { await this.refreshSession().toPromise(); }
    catch { /* Keep the saved session on temporary network/server failures. */ }
  }

  /**
   * @name forgotPassword
   * @description forgot password base on email address
   */
  /**
   * @name resetPassword
   * @description Reset password for forgot password
   */

  signup(email: any, password: any, invite_code: any) {
    const body = { email, password, invite_code };
    const endPoint = `${environment.apiUrl}/api/v1/operators/users/signup`;
    return this.http.post<any>(endPoint, body).pipe(
      switchMap((response: any) => {
        if (!response?.id) return throwError(new Error(response?.errorMessage || 'Signup failed'));
        return this.login(email, password);
      })
    );
  }


  forgotPassword(email: any) {
    const endPoint = `${environment.apiUrl}/api/v1/users/${email}/mail-forgot-password`;
    return this.http.post<any>(endPoint, {});
  }

  resetPassword(email: any, password: string, code: string) {
    const endPoint = `${environment.apiUrl}/api/v1/users/forgot-password-reset`;
    return this.http.post<any>(endPoint, {
      email,
      password,
      code,
    });
  }
}
